import {
    EmploymentType,
    JobApplicationStatus,
    JobPostingDTO,
    JobPostingStatus,
    employmentTypeLabel,
    formatDate,
} from "@pentacode/core";
import { LitElement, css, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { alert } from "../../elements/alert-dialog";
import "../../elements/scroller";
import { app } from "../../init";
import { Routing, routeProperty } from "../../mixins/routing";
import { StateMixin } from "../../mixins/state";
import { shared } from "../../styles";
import "./components/recruiting-posting";
import type { RecruitingPosting } from "./components/recruiting-posting";

type Posting = RecruitingPosting["posting"];

@customElement("ptc-recruiting")
export class RecruitingPostings extends Routing(StateMixin(LitElement)) {
    readonly routePattern = /^recruiting(?:\/(?<id>[^/]+))?/;

    get routeTitle() {
        return !this._selectedPosting
            ? "Stellenausschreibungen verwalten"
            : `Stellenausschreibungen: ${this._selectedPosting.title}`;
    }

    get helpPage() {
        return "/handbuch/recruiting";
    }

    @routeProperty({ arg: "id" })
    private _selectedPostingId: string;

    @state()
    private _postings: Posting[] = [];

    @state()
    private _archivedPostings: Posting[] = [];

    @state()
    private _loading = false;

    @state()
    private _selectedPosting: Posting | null = null;

    private _draftPosting: Posting | null = null;

    @state()
    private _archiveExpanded = false;

    async updated(changes: Map<string, unknown>) {
        if (changes.has("active") && this.active) {
            await this._load();
        }
    }

    handleRefresh() {
        if (this._loading || this._editingPosting) {
            return;
        }
        void this._load({ preserveEditor: true });
    }

    private get _editingPosting(): boolean {
        return (
            this._selectedPostingId === "new" ||
            !!this.renderRoot.querySelector<RecruitingPosting>("ptc-recruiting-posting")?.editing
        );
    }

    handleRoute() {
        if (this._selectedPostingId === "new") {
            this._selectedPosting =
                this._draftPosting ??
                new JobPostingDTO({
                    id: null,
                    uuid: "",
                    title: "",
                    description: "",
                    status: JobPostingStatus.Published,
                    companyId: app.company?.id || 0,
                    venueId: app.venues[0]?.id || 0,
                    payType: "hourly",
                    payAmount: undefined,
                    start: null,
                    end: null,
                    employmentType: EmploymentType.Regular,
                    hoursPerWeek: null,
                    imageUrls: [],
                    positions: [],
                    applications: [],
                });
            return;
        }

        this._draftPosting = null;

        // Legacy /recruiting/archive URL: expand the archive dropdown instead of showing the empty state
        if (this._selectedPostingId === "archive") {
            this._selectedPosting = null;
            this._archiveExpanded = true;
            return;
        }

        const id = Number(this._selectedPostingId);
        this._selectedPosting =
            this._postings.find((p) => p.id === id) || this._archivedPostings.find((p) => p.id === id) || null;

        // Keep the archive dropdown open when an archived posting is selected
        if (this._selectedPosting?.status === JobPostingStatus.Archived) {
            this._archiveExpanded = true;
        }
    }

    private async _load({ preserveEditor = false }: { preserveEditor?: boolean } = {}) {
        this._loading = true;

        try {
            const postings = await app.api.getJobPostings({ includeArchived: true });
            this._postings = postings.filter((posting) => posting.status === JobPostingStatus.Published);
            this._archivedPostings = postings.filter((posting) => posting.status === JobPostingStatus.Archived);
            // Editing may start while a refresh request is in flight.
            if (!preserveEditor || (this.active && !this._editingPosting)) {
                this.handleRoute();
            }
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    static styles = [
        shared,
        css`
            .sub-menu {
                padding: 0;
                width: 20em;
            }
        `,
    ];

    private _renderPostingItem(posting: Posting) {
        return html`
            <div
                class="double-padded border-bottom click ${this._selectedPostingId === posting.id?.toString()
                    ? "bold blue colored-text"
                    : ""}"
                @click=${() => this.go(`recruiting/${posting.id}`)}
            >
                <div class="bottom-margined">${posting.title}</div>
                <div class="tiny pills">
                    <div class="pill">
                        <ptc-ui-icon name="icon.object.contract"></ptc-ui-icon> ${employmentTypeLabel(
                            posting.employmentType
                        )}
                    </div>

                    <div class="pill">
                        <ptc-ui-icon name="icon.object.time"></ptc-ui-icon> Ab
                        ${posting.start ? formatDate(posting.start) : " Sofort"}
                    </div>

                    ${posting.positions.map(
                        (pos) => html`
                            <div class="pill" style="--color-highlight: ${app.getPositionColor(pos)}">
                                <ptc-ui-icon name="icon.object.position"></ptc-ui-icon>
                                ${pos.name}
                            </div>
                        `
                    )}

                    <div class="orange pill">
                        <ptc-ui-icon name="icon.object.application-photo"></ptc-ui-icon>
                        ${posting.applications.filter((a) => a.status === JobApplicationStatus.Open).length} Bew.
                    </div>
                </div>
            </div>
        `;
    }

    private _renderMainContent() {
        if (this._selectedPosting) {
            return html`
                <ptc-recruiting-posting
                    class="stretch"
                    .posting=${this._selectedPosting!}
                    @posting-edited=${() => this._load()}
                    @posting-edit-canceled=${() => this.handleRoute()}
                    @posting-created=${async (e: CustomEvent<{ posting: Posting }>) => {
                        this._draftPosting = null;
                        await this._load();
                        this.go(`recruiting/${e.detail.posting.id}`);
                    }}
                    @posting-create-canceled=${() => {
                        this._draftPosting = null;
                        this.go("recruiting");
                    }}
                    @posting-deleted=${async () => {
                        await this._load();
                        this.go("recruiting");
                    }}
                    @posting-archived=${async () => {
                        await this._load();
                        this.go("recruiting");
                    }}
                    @posting-reactivated=${async (e: CustomEvent<{ posting: Posting }>) => {
                        await this._load();
                        this.go(`recruiting/${e.detail.posting.id}`);
                    }}
                    @posting-copy-created=${(e: CustomEvent<{ posting: Posting }>) => {
                        this._draftPosting = e.detail.posting;
                        this._selectedPosting = e.detail.posting;
                        this.go("recruiting/new");
                    }}
                ></ptc-recruiting-posting>
            `;
        }

        return html`
            <div class="stretch faded double-padded centering vertical layout">
                <ptc-ui-icon name="icon.status.nothing-selected" class="giant"></ptc-ui-icon>
                <div>Keine Ausschreibung gewählt.</div>
            </div>
        `;
    }

    render() {
        return html`
            <div class="fullbleed horizontal layout">
                <div class="sub-menu relative">
                    <ptc-scroller class="fullbleed">
                        <div
                            class="double-padded border-bottom click ${this._selectedPostingId === "new"
                                ? "bold blue colored-text"
                                : ""}"
                            @click=${() => {
                                this._draftPosting = null;
                                this.go("recruiting/new");
                            }}
                        >
                            <ptc-ui-icon name="icon.action.add"></ptc-ui-icon> Neue Ausschreibung
                        </div>
                        ${[...this._postings]
                            .sort((a, b) => (a.title < b.title ? -1 : 1))
                            .map((posting) => this._renderPostingItem(posting))}

                        <div
                            class="double-padded border-top click horizontal center-aligning layout ${this
                                ._archiveExpanded
                                ? "bold"
                                : ""}"
                            @click=${() => (this._archiveExpanded = !this._archiveExpanded)}
                        >
                            <div class="stretch">
                                <ptc-ui-icon name="icon.action.archive"></ptc-ui-icon> Stellenarchiv
                            </div>
                            ${this._archiveExpanded
                                ? html`<ptc-ui-icon name="icon.toggle.expand-active"></ptc-ui-icon>`
                                : html`<ptc-ui-icon name="icon.toggle.expand"></ptc-ui-icon>`}
                        </div>

                        ${this._archiveExpanded
                            ? this._archivedPostings.length
                                ? [...this._archivedPostings]
                                      .sort((a, b) => (a.title < b.title ? -1 : 1))
                                      .map((posting) => this._renderPostingItem(posting))
                                : html`
                                      <div class="double-padded border-bottom subtle small">
                                          Keine archivierten Ausschreibungen.
                                      </div>
                                  `
                            : ""}
                    </ptc-scroller>
                </div>

                ${this._renderMainContent()}
            </div>

            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>
        `;
    }
}
